import i18next, { type TFunction } from "i18next";
import moment from "moment";
import { displayDate } from "../formatting/displayDate";
import { formatNumber } from "../formatting/formatNumber";
import { momentLocaleFor } from "../formatting/i18nFormatters";
import type { EntityTableColumn } from "./EntityTable";

export type ColumnFilterPart = "search" | "multi" | "single" | "date" | "number";

export type FilterableColumn = Omit<EntityTableColumn, "key" | "label">;

export const activeFilterParts = (column: FilterableColumn): ColumnFilterPart[] => {
  const dates = column.dateRangeConfig;
  const numbers = column.numberRangeConfig;
  const parts: [ColumnFilterPart, boolean][] = [
    ["search", !!column.currentSearchValue?.trim()],
    ["multi", (column.multiSelectConfig?.selectedValues?.length ?? 0) > 0],
    ["single", !!column.singleSelectConfig?.selectedValue?.value],
    ["date", !!dates && !!(dates.preset || dates.fromDate?.trim() || dates.toDate?.trim())],
    ["number", !!numbers && (numbers.from != null || numbers.to != null)],
  ];
  return parts.filter(([, active]) => active).map(([part]) => part);
};

export const filterValueText = (t: TFunction, column: FilterableColumn, part: ColumnFilterPart): string => {
  switch (part) {
    case "search":
      return `«${column.currentSearchValue?.trim() ?? ""}»`;
    case "multi":
      return [...new Set((column.multiSelectConfig?.selectedValues ?? []).map((value) => value.label))].join(", ");
    case "single": {
      const config = column.singleSelectConfig;
      const chosen = config?.selectedValue;
      return config?.options.find((option) => option.value === chosen?.value)?.label ?? chosen?.label ?? "";
    }
    case "date": {
      const dates = column.dateRangeConfig;
      const preset = dates?.presets?.find((choice) => choice.value === dates.preset);
      if (dates?.preset) return preset?.label ?? dates.preset;
      const month = wholeMonth(dates?.fromDate, dates?.toDate);
      if (month) return month;
      return range(t, dates?.fromDate ? dateText(dates.fromDate) : null, dates?.toDate ? dateText(dates.toDate) : null, "date");
    }
    case "number": {
      const numbers = column.numberRangeConfig;
      return range(
        t,
        numbers?.from != null ? formatNumber(numbers.from) : null,
        numbers?.to != null ? formatNumber(numbers.to) : null,
        "number",
      );
    }
  }
};

export const chosenFilterValueCount = (column: EntityTableColumn): number =>
  activeFilterParts(column).reduce(
    (count, part) => count + (part === "multi" ? (column.multiSelectConfig?.selectedValues?.length ?? 0) : 1),
    0,
  );

export const hasValueFilter = (column: EntityTableColumn): boolean =>
  !!(column.multiSelectConfig || column.singleSelectConfig || column.dateRangeConfig || column.numberRangeConfig);

export const filtersAsText = (column: EntityTableColumn): boolean =>
  !!column.onSearch && (!!column.isTextColumn || !hasValueFilter(column));

export const isFilterable = (column: EntityTableColumn): boolean => !!column.onSearch || hasValueFilter(column);

export const VALUE_SEARCH_FROM_OPTION_COUNT = 8;

export const SEARCH_MAX_LENGTH = 200;

export const clearFilterPart = (column: EntityTableColumn, part: ColumnFilterPart): void => {
  switch (part) {
    case "search":
      column.onSearch?.("");
      return;
    case "multi":
      void column.multiSelectConfig?.onSelect([]);
      return;
    case "single":
      void column.singleSelectConfig?.onSelect({ value: "", label: "" });
      return;
    case "date":
      void column.dateRangeConfig?.onPresetChange?.(undefined);
      void column.dateRangeConfig?.onFromDateChange(undefined);
      void column.dateRangeConfig?.onToDateChange(undefined);
      return;
    case "number":
      void column.numberRangeConfig?.onFromChange(undefined);
      void column.numberRangeConfig?.onToChange(undefined);
  }
};

const dateText = (date: string) => displayDate(date, momentLocaleFor(i18next.language));

const wholeMonth = (fromDate?: string | null, toDate?: string | null): string | null => {
  if (!fromDate || !toDate) return null;
  const from = moment(fromDate, "YYYY-MM-DD", true);
  const to = moment(toDate, "YYYY-MM-DD", true);
  const spansOneMonth =
    from.isValid() && to.isValid() && from.date() === 1 && to.isSame(from.clone().endOf("month"), "day");
  return spansOneMonth ? from.locale(momentLocaleFor(i18next.language)).format("MMMM YYYY") : null;
};

const range = (t: TFunction, from: string | null, to: string | null, kind: "date" | "number"): string => {
  if (from && to) return `${from} – ${to}`;
  if (from) return kind === "date" ? String(t("from {{date}}", { date: from })) : String(t("at least {{value}}", { value: from }));
  return kind === "date" ? String(t("until {{date}}", { date: to })) : String(t("at most {{value}}", { value: to }));
};
